import { useEffect, useRef, useCallback } from 'react';
import { size, merge, noop } from 'lodash';
import { Impressionable } from '@fiverr-private/impressionable';
import { useObserver } from '@fiverr-private/orca';
import { ReportingChannelType } from '../../events/reporter/constants';

/**
 * Hook for integrating impression metric reports.
 *
 * @param {Function} buildImpressionData
 * @param {String} eventKey
 * @returns {{containerRef: Object}}
 */
const useImpressions = ({ buildImpressionData = noop, eventKey }) => {
    const containerRef = useRef();
    const impressionableRef = useRef();

    const enrich = useCallback(
        (data, child) => {
            const { events } = ReportingChannelType.IMPRESSION;
            const eventBuilder = events[eventKey];
            const enrichmentData = buildImpressionData(child);
            const eventParams = merge({}, data, enrichmentData);

            return eventBuilder(eventParams);
        },
        [buildImpressionData, eventKey]
    );

    const init = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        if (impressionableRef.current) {
            impressionableRef.current.die();
        }

        impressionableRef.current = new Impressionable({
            container: containerRef.current,
            enrich,
        });
    }, [impressionableRef, containerRef, enrich]);

    useEffect(() => {
        init();
    }, [init]);

    const collectImpressions = () => {
        if (!impressionableRef.current) {
            return;
        }

        const hasImpressions = size(impressionableRef.current.unimpressed) > 0;

        hasImpressions && impressionableRef.current.collect();
    };

    useObserver({
        observableRef: containerRef,
        onObserve: collectImpressions,
    });

    return { containerRef };
};

export default useImpressions;
